import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, TrendingDown, ChevronRight, ArrowRight, Tag, BadgeCheck } from 'lucide-react';
import ListingCard from '../components/listing/ListingCard';
import { CategoryIcon } from '../data/categoryIcons';
import { listingService } from '../services/listingService';
import { categoryService } from '../services/categoryService';
import { useLanguage } from '../context/LanguageContext';

const PRICES = [499, 479, 459, 439, 419, 399, 379, 359, 339, 319];

function HeroPrice() {
  const [idx, setIdx] = useState(0);
  const [show, setShow] = useState(true);

  useEffect(() => {
    const t = setInterval(() => {
      setShow(false);
      setTimeout(() => { setIdx(i => (i + 1) % PRICES.length); setShow(true); }, 220);
    }, 2200);
    return () => clearInterval(t);
  }, []);

  return (
    <span
      style={{
        opacity: show ? 1 : 0,
        transform: show ? 'translateY(0)' : 'translateY(-16px)',
        transition: 'opacity .22s ease, transform .22s ease',
        display: 'inline-block',
      }}
    >
      €{PRICES[idx]}
    </span>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const { t, tCat } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState([]);
  const [featuredListings, setFeaturedListings] = useState([]);
  const [recentListings, setRecentListings] = useState([]);

  useEffect(() => {
    categoryService.getAll().then(setCategories).catch(() => {});
    listingService.getAll({ isFeatured: true, pageSize: 3 }).then(d => setFeaturedListings(d?.items || d || [])).catch(() => {});
    listingService.getAll({ pageSize: 6 }).then(d => setRecentListings(d?.items || d || [])).catch(() => {});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
  };

  return (
    <div>

      {/* HERO */}
      <section
        className="relative flex flex-col items-center justify-center text-center overflow-hidden"
        style={{ minHeight: '100vh', background: '#06060e' }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 10%, rgba(10,132,255,0.14) 0%, transparent 70%)' }}
        />

        <div className="relative z-10 max-w-4xl mx-auto px-6 py-20 flex flex-col items-center">

          <p className="text-sm font-medium mb-5" style={{ color: '#0a84ff', letterSpacing: '0.06em' }}>
            {t('home.eyebrow')}
          </p>

          <h1
            className="font-bold text-white tracking-tight leading-none mb-5"
            style={{ fontSize: 'clamp(2.6rem, 6vw, 5rem)' }}
          >
            {t('home.headline1')}<br />
            <span style={{ color: '#0a84ff' }}>{t('home.headline2')}</span>
          </h1>

          <div className="my-6">
            <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>
              {t('home.current_price')}
            </p>
            <p
              className="font-bold text-white tabular-nums"
              style={{ fontSize: 'clamp(3.2rem, 9vw, 7.5rem)', lineHeight: 1, letterSpacing: '-0.04em' }}
            >
              <HeroPrice />
            </p>
            <p style={{ color: 'rgba(255,255,255,0.25)', fontSize: 13, marginTop: 8, textDecoration: 'line-through' }}>
              {t('home.starting_price')}
            </p>
          </div>

          <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 18, fontWeight: 300, lineHeight: 1.6, maxWidth: 400, marginBottom: 36 }}>
            {t('home.subtitle1')}<br />{t('home.subtitle2')}
          </p>

          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Link
              to="/register"
              className="flex items-center gap-2 font-medium px-7 py-3 rounded-full text-sm transition-colors"
              style={{ background: '#0a84ff', color: '#fff' }}
              onMouseEnter={e => e.currentTarget.style.background = '#0071e3'}
              onMouseLeave={e => e.currentTarget.style.background = '#0a84ff'}
            >
              {t('home.cta_start')} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/search"
              className="font-medium px-7 py-3 rounded-full text-sm transition-colors"
              style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.85)', border: '1px solid rgba(255,255,255,0.12)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.14)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
            >
              {t('home.cta_browse')}
            </Link>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 opacity-20">
          <div
            className="w-px bg-white mx-auto"
            style={{ height: 36, animation: 'scrollHint 1.8s ease-in-out infinite' }}
          />
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 pointer-events-none"
          style={{ height: 280, background: 'linear-gradient(to bottom, transparent 0%, #06060e 30%, #ffffff 100%)' }}
        />
      </section>

      {/* STATS */}
      <section className="border-b border-gray-100 py-14">
        <div className="max-w-4xl mx-auto px-6 grid grid-cols-3 gap-6 text-center">
          {[
            { value: '12.400+', label: t('home.stat_listings') },
            { value: '34.000+', label: t('home.stat_members') },
            { value: '€ 2.1M+', label: t('home.stat_sales') },
          ].map(s => (
            <div key={s.label}>
              <p className="text-3xl md:text-4xl font-bold text-apple-gray-dark tracking-tight">{s.value}</p>
              <p className="text-sm text-apple-gray-mid mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section style={{ background: '#f5f5f7' }} className="py-14">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-sm font-medium text-apple-blue mb-2">{t('home.how_eyebrow')}</p>
            <h2 className="section-title mb-2">{t('home.how_title')}</h2>
            <p className="text-apple-gray-mid text-base font-light max-w-lg mx-auto">
              {t('home.how_subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {[
              { step: '01', Icon: Tag,         title: t('home.step1_title'), desc: t('home.step1_desc') },
              { step: '02', Icon: TrendingDown, title: t('home.step2_title'), desc: t('home.step2_desc') },
              { step: '03', Icon: BadgeCheck,   title: t('home.step3_title'), desc: t('home.step3_desc') },
            ].map(({ step, Icon, title, desc }) => (
              <div key={step} className="bg-white rounded-2xl p-6 relative overflow-hidden" style={{ boxShadow: '0 2px 20px rgba(0,0,0,0.06)' }}>
                <span
                  className="absolute top-3 right-5 font-black select-none leading-none"
                  style={{ fontSize: '4.5rem', color: '#f5f5f7' }}
                >
                  {step}
                </span>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: 'rgba(0,113,227,0.08)' }}>
                  <Icon style={{ width: 18, height: 18, strokeWidth: 1.5, color: '#0071e3' }} />
                </div>
                <h3 className="text-base font-semibold text-apple-gray-dark mb-1">{title}</h3>
                <p className="text-sm text-apple-gray-mid leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEARCH BAR */}
      <section className="py-14 text-center">
        <p className="text-sm font-medium text-apple-blue mb-2">{t('home.search_eyebrow')}</p>
        <h2 className="text-3xl font-semibold text-apple-gray-dark mb-6">{t('home.search_title')}</h2>
        <form onSubmit={handleSearch} className="max-w-2xl mx-auto px-6 flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-apple-gray-mid w-4 h-4" />
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="iPhone, MacBook, Berlin..."
              className="w-full pl-11 pr-4 py-3.5 rounded-2xl border border-gray-200 text-apple-gray-dark text-sm focus:outline-none focus:border-apple-blue focus:ring-2 focus:ring-apple-blue/20"
              style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}
            />
          </div>
          <button type="submit" className="bg-apple-blue hover:bg-apple-blue-hover text-white px-6 py-3.5 rounded-2xl font-medium text-sm transition-colors">
            {t('home.search_btn')}
          </button>
        </form>
        <div className="flex flex-wrap items-center justify-center gap-2 mt-3">
          {['iPhone', 'MacBook', 'Fahrrad', 'Samsung', 'Berlin'].map(t => (
            <button
              key={t}
              onClick={() => navigate(`/search?q=${t}`)}
              className="text-xs text-apple-gray-mid hover:text-apple-blue border border-gray-200 hover:border-apple-blue/30 px-3 py-1.5 rounded-full transition-colors"
            >
              {t}
            </button>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      {categories.length > 0 && (
        <section style={{ background: '#f5f5f7' }} className="py-16">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-semibold text-apple-gray-dark">{t('home.categories_title')}</h2>
              <Link to="/search" className="text-sm text-apple-blue hover:underline flex items-center gap-1">
                {t('home.see_all')} <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-5 md:grid-cols-10 gap-3">
              {categories.map(cat => (
                <Link
                  key={cat.id}
                  to={`/search?category=${cat.slug}`}
                  className="flex flex-col items-center gap-2 p-3 rounded-2xl hover:bg-white transition-all group"
                >
                  <div className="w-12 h-12 rounded-2xl bg-white group-hover:bg-apple-blue group-hover:text-white text-apple-gray-mid flex items-center justify-center transition-all duration-200" style={{ boxShadow: '0 1px 6px rgba(0,0,0,0.07)' }}>
                    <CategoryIcon id={cat.slug} className="w-5 h-5" />
                  </div>
                  <span className="text-xs text-apple-gray-mid font-medium text-center leading-tight">{tCat(cat.slug)}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FEATURED LISTINGS */}
      {featuredListings.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-16">
          <div className="flex items-center justify-between mb-8">
            <div>
              <p className="text-sm font-medium text-apple-blue mb-1">{t('home.featured_eyebrow')}</p>
              <h2 className="text-2xl font-semibold text-apple-gray-dark">{t('home.featured_title')}</h2>
            </div>
            <Link to="/search?featured=true" className="text-sm text-apple-blue hover:underline flex items-center gap-1">
              {t('home.see_all')} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {featuredListings.map(l => <ListingCard key={l.id} listing={l} featured />)}
          </div>
        </section>
      )}

      {/* RECENT LISTINGS */}
      {recentListings.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-10">
          <div className="flex items-center justify-between mb-8">
            <div>
              <p className="text-sm font-medium text-apple-blue mb-1">{t('home.recent_eyebrow')}</p>
              <h2 className="text-2xl font-semibold text-apple-gray-dark">{t('home.recent_title')}</h2>
            </div>
            <Link to="/search" className="text-sm text-apple-blue hover:underline flex items-center gap-1">
              {t('home.see_all')} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {recentListings.map(l => <ListingCard key={l.id} listing={l} />)}
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="py-16 text-center max-w-2xl mx-auto px-6">
        <p className="text-sm font-medium text-apple-blue mb-2">{t('home.cta2_eyebrow')}</p>
        <h2 className="text-3xl font-semibold text-apple-gray-dark tracking-tight mb-3">
          {t('home.cta2_title').split('\n').map((line, i) => (
            <React.Fragment key={i}>{line}{i === 0 && <br />}</React.Fragment>
          ))}
        </h2>
        <p className="text-apple-gray-mid text-base font-light mb-7">
          {t('home.cta2_subtitle')}
        </p>
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <Link to="/register" className="bg-apple-blue hover:bg-apple-blue-hover text-white px-7 py-3 rounded-full font-medium text-sm flex items-center gap-2 transition-colors">
            {t('home.cta2_start')} <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/search" className="bg-apple-gray hover:bg-gray-200 text-apple-gray-dark px-7 py-3 rounded-full font-medium text-sm transition-colors">
            {t('home.cta2_browse')}
          </Link>
        </div>
      </section>

    </div>
  );
}
